import { KeyboardEvent, useCallback, useEffect, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import FooterModal from "@components/FooterModal/FooterModal";
import LoginModalFooter from "@components/LoginModalFooter/LoginModalFooter";
import CustomButton from "@components/shared/FormElements/CustomButton/CustomButton";
import InputLabel from "@components/shared/FormElements/InputLabel/InputLabel";
import PasswordLabel from "@components/shared/FormElements/InputPassword/InputPassword";
import Loader from "@components/shared/Loader/Loader";
import Modals from "@components/shared/Modal/Modal";
import { AVAILIO_LOGO } from "@constants/assets";
import { CONSTANTS } from "@constants/index";
import { timerConstants } from "@constants/login";
import { routeEnum, supportEnum } from "@enums/route.enum";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import authSlice from "@slices/auth.slice";
import localSlice from "@slices/local.slice";
import modalSlice from "@slices/modal.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";

const LoginModalComponent = () => {
  const [loginStates, setLoginStates] = useState({
    emailValidation: false,
    passwordValidation: false,
  });

  const params = useSelector((state: RootState) => state.auth.params);
  const { authModal } = useSelector((state: RootState) => state.auth);

  const { t } = useTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [isResetDisabled, setIsResetDisabled] = useState(false);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [timer, setTimer] = useState(timerConstants.startTime);
  const validateEmail = useCallback((value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }, []);

  const validatePassword = useCallback((value: string) => {
    const isValid = value != null && value.trim() !== "";
    return isValid;
  }, []);

  const loginCall = useCallback(
    async (email: string, password: string) => {
      setIsFetching(true);

      const data = await API_SERVICES.LOGIN_USER(email, password);
      setIsFetching(false);
      return data;
    },
    [t]
  );

  const resetPasswordCall = useCallback(
    async (email: string) => {
      setIsFetching(true);
      const data = await API_SERVICES.RESET_PASSWORD(email);
      if (data?.ok) {
        setIsResetDisabled(true);

        setTimer(timerConstants.startTime); // Reset timer to 60 seconds
        setIsFetching(false);

        // Start timer countdown
        const countdown = setInterval(() => {
          setTimer(prev => {
            if (prev <= 1) {
              clearInterval(countdown);
              setIsResetDisabled(false);
              return 0;
            }
            return prev - 1;
          });
        }, timerConstants.interval);
      } else {
        setIsFetching(false);
        setIsResetDisabled(false);
      }

      return data;
    },
    [t]
  );

  const onLogin = useCallback(async () => {
    const emailValid = validateEmail(email);
    const passwordValid = validatePassword(password);
    if (emailValid && passwordValid) {
      const data = await loginCall(email, password);
      const responseData = handleResponse(data);
      if (responseData?.ok) {
        dispatch(
          authSlice.actions.passAuth({ ...authModal, loginCompleted: true })
        );
        dispatch(
          localSlice.actions.userAuthenticated({
            isAuthenticated: true,
            refresh_token: responseData.data.refresh_token,
            token: responseData.data.token,
          })
        );
        dispatch(modalSlice.actions.updateLoginModal(false));
        dispatch(modalSlice.actions.updateAuthModal(false));
        commonUtils.toastService(
          t("successfullLogin"),
          CONSTANTS.TOAST_TYPE.SUCCESS
        );
        dispatch(authSlice.actions.firstlogin(responseData.data.firstLogin));
        if (responseData.data.firstLogin) {
          dispatch(modalSlice.actions.updateMemberListing(true));
        }
      }
    } else {
      setLoginStates(prev => ({
        ...prev,
        emailValidation: !emailValid,
        passwordValidation: !passwordValid,
      }));
    }
  }, [
    email,
    password,
    loginCall,
    validateEmail,
    validatePassword,
    setLoginStates,
  ]);

  useEffect(() => {
    setLoginStates(prev => ({
      ...prev,
      emailValidation: false,
      passwordValidation: false,
    }));
  }, [email]);

  useEffect(() => {
    if (params.userEmail && params.verificationCode) {
      setEmail(decodeURIComponent(params.userEmail));
    }
  }, [params.userEmail, params.verificationCode]);

  const onEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === CONSTANTS.KEYBOARD_KEYS.ENTER && !isFetching) {
      event.preventDefault();
      if (!authModal.loginCompleted && email) {
        onLogin();
      }
    }
  };

  const errorMessageBlock = () => {
    const errorMessages = [
      {
        condition: loginStates.emailValidation,
        message: t("pleaseEnterValidEmail"),
      },
    ];

    return (
      <div>
        {errorMessages.map(
          (error, index) =>
            error.condition && (
              <div key={index} className="ps-3 form-text-error">
                {error.message}
              </div>
            )
        )}
      </div>
    );
  };
  const passwordErrorBlock = () => {
    const errorMessages = [
      { condition: loginStates.passwordValidation, message: t("passwordReq") },
    ];

    return (
      <div>
        {errorMessages.map(
          (error, index) =>
            error.condition && (
              <div key={index} className="ps-3 form-text-error">
                {error.message}
              </div>
            )
        )}
      </div>
    );
  };

  const closeModal = () => {
    dispatch(
      authSlice.actions.passAuth({
        loginCompleted: false,
        passwordSection: false,
      })
    );
    dispatch(modalSlice.actions.updateLoginModal(false));
    dispatch(modalSlice.actions.updateAuthModal(false));
    if (pathname.includes(supportEnum.Auth)) navigate(routeEnum.Home);
    dispatch(
      authSlice.actions.passParams({
        userEmail: "",
        verificationCode: "",
      })
    );
  };

  const onRegister = () => {
    dispatch(modalSlice.actions.updateRegisterModal(true));
    setEmail("");
    setPassword("");
    setLoginStates({
      emailValidation: false,
      passwordValidation: false,
    });
  };

  const resetPassword = useCallback(async () => {
    const emailValid = validateEmail(email);

    if (emailValid) {
      const data = await resetPasswordCall(email);
      const responseData = handleResponse(data);

      if (responseData?.ok) {
        commonUtils.toastService(
          t("passwordModal.resetLinkSent"),
          CONSTANTS.TOAST_TYPE.SUCCESS
        );
      }
    } else {
      setLoginStates(prev => ({
        ...prev,
        emailValidation: !emailValid,
      }));
    }
  }, [
    email,
    resetPasswordCall,
    validateEmail,
    validatePassword,
    setLoginStates,
  ]);

  const openPasswordModal = () => {
    dispatch(
      authSlice.actions.passAuth({
        loginCompleted: true,
        passwordSection: true,
      })
    );
  };

  const onEmailChange = (value: string) => {
    setEmail(value);
    setIsResetDisabled(false);
  };

  return (
    <Modals
      flag={true}
      footerClassName="fg-modal-footer"
      closeModals={closeModal}
      onClose={closeModal}
      modalClassName={authModal.loginCompleted ? "sessionModal" : "loginModal"}
      contentClassName="fg-modal-body-bg"
      footerContent={
        !authModal.loginCompleted ? (
          <LoginModalFooter onRegister={onRegister} />
        ) : (
          <FooterModal />
        ) // Replace LoginModalFooter with LoginFooter
      }
    >
      <>
        {isFetching && <Loader isLoading={true} />}
        <div
          className="container-fluid fg-z-index-1 position-relative"
          data-testid="login-header"
        >
          <div className="fg-logo-within-shape">
            <img
              src={AVAILIO_LOGO}
              className="fg-logo-img"
              alt={t("availioTitle")}
            />
          </div>
        </div>
        <div className="container-fluid fg-z-index-1 position-relative modal-form-content">
          <div className="row">
            <div className="col-sm-12 col-md-6 col-xl-4 offset-md-3 offset-xl-4">
              <h1>
                {authModal.passwordSection
                  ? `${t("forgotPassword")}`
                  : `${t("login")}`}
              </h1>

              {!authModal.passwordSection && (
                <div className="alert alert-primary fg-FS-14" role="alert">
                  {t("loginInfoMessage")}
                </div>
              )}

              {authModal.passwordSection && (
                <p className="mb-3">{t("forgotPasswordEmailVerify")}</p>
              )}

              <div className={"form-floating mb-3 fg-input-with-icon"}>
                <InputLabel
                  isError={loginStates.emailValidation}
                  isIcon={true}
                  labelText={t("MainEMail")}
                  onChange={e => onEmailChange(e.target.value)}
                  onKeyDown={onEnter}
                  spellCheck={false}
                  testId="emailField"
                  value={email}
                />

                {errorMessageBlock()}
              </div>
              {!authModal.passwordSection && (
                <>
                  <div className={"form-floating mb-3 fg-input-with-icon"}>
                    <PasswordLabel
                      isError={loginStates.passwordValidation}
                      isIcon={true}
                      labelText={t("passwordLabel")}
                      onChange={e => setPassword(e.target.value)}
                      onKeyDown={onEnter}
                      testId="password"
                      showStrength={false}
                    />
                    {passwordErrorBlock()}
                  </div>
                  <button
                    data-testid="forgot-password"
                    className="fg-button-override fg-read-more-btn mb-3"
                    onClick={openPasswordModal}
                  >
                    {t("forgotPassword")}
                  </button>
                </>
              )}
              {!authModal.passwordSection && (
                <CustomButton
                  onClick={() => onLogin()}
                  disabled={!email || !password || isFetching}
                  buttonText={t("login")}
                />
              )}
              {authModal.passwordSection && isResetDisabled && (
                <div className="reset-timer">
                  {t("passwordModal.resetLinkSentTimer", { timer: timer })}
                </div>
              )}
              {authModal.passwordSection && (
                <CustomButton
                  onClick={() => resetPassword()}
                  disabled={!email || isFetching || isResetDisabled}
                  buttonText={t("resetPassord")}
                />
              )}
            </div>
          </div>
        </div>
      </>
    </Modals>
  );
};

const LoginModal = withTranslation()(LoginModalComponent);

export default LoginModal;
